// src/components/settings/TechPermissionsSettings.js
//
// Org-level technician toggles, all OFF by default. Each writes a single
// top-level org flag read by firestore.rules + the matching UI gates:
//   • techJobCreationEnabled        — may techs create jobs? (JobFormModal, Add Job button)
//   • techCustomerManagementEnabled — may techs create/edit customers? (CustomerFormModal,
//                                     Add Customer button; financials stay hidden, no delete)
// Both govern NEW activity only — existing jobs/customers are never affected,
// and turning a flag off later leaves prior work alone.
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ShieldCheck, AlertTriangle, CheckCircle } from 'lucide-react';

export default function TechPermissionsSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    const [techCanCreateJobs, setTechCanCreateJobs] = useState(false);
    const [techCanManageCustomers, setTechCanManageCustomers] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saved,    setSaved]    = useState(false);
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            if (snap.exists()) {
                setTechCanCreateJobs(snap.data().techJobCreationEnabled === true);
                setTechCanManageCustomers(snap.data().techCustomerManagementEnabled === true);
            }
            setLoading(false);
        });
    }, [orgId]);

    const handleSave = async () => {
        setError('');
        setIsSaving(true);
        try {
            // Named top-level fields only — this write touches just these two flags.
            await updateDoc(doc(db, 'organizations', orgId), {
                techJobCreationEnabled: techCanCreateJobs,
                techCustomerManagementEnabled: techCanManageCustomers,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            console.error('Error saving technician permissions:', err);
            setError('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    return (
        <div className="p-6 space-y-6 max-w-2xl">
            <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-gray-500" /> Technician Permissions
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Control what your Field Technicians can do. These settings only affect new
                    activity — existing jobs are never changed.
                </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Let technicians create jobs</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        When on, a Field Technician can open the job form and create a job. It goes
                        live immediately and is assigned to them; they can add other people or a
                        route group. Turning this off later only stops new jobs — it never removes
                        jobs they already made.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setTechCanCreateJobs(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${techCanCreateJobs ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${techCanCreateJobs ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Let technicians create and edit customers</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        When on, a Field Technician can add a new customer and edit any existing one
                        — the same customer details your office staff can edit. They can never delete
                        a customer, and financial figures (lifetime value, balances, payment history)
                        stay hidden from them. Turning this off later only stops new changes.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setTechCanManageCustomers(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${techCanManageCustomers ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${techCanManageCustomers ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                {saved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} /> Saved
                    </span>
                )}
            </div>
        </div>
    );
}
